import type { ClientAccessImpact, ClientAccessSettings } from "@repo/shared/types";
import { Info, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { describeClientAccessConsequences } from "./client-access-consequences";
import { T } from "./client-access-impact-panel.i18n";

type Props = {
  target: ClientAccessSettings;
  impact: ClientAccessImpact | null;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
};

/** QL-380 — the consequences of switching to `target`, worded from the server's buckets for that combination. */
export function ClientAccessImpactPanel({ target, impact, isLoading, isRetrying, onRetry }: Props) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  if (isLoading) return <Skeleton className="h-16 w-full" />;

  if (!impact) {
    return (
      <Alert variant="warning">
        <TriangleAlert />
        <AlertTitle>{t.title}</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span>{t.unavailable}</span>
          <Button size="sm" variant="outline" onClick={onRetry} disabled={isRetrying}>
            {t.retry}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const consequences = describeClientAccessConsequences(target, impact, userLanguage);
  const hasWarning = consequences.some((c) => c.tone === "warning");

  return (
    <Alert variant={hasWarning ? "warning" : "info"}>
      {hasWarning ? <TriangleAlert /> : <Info />}
      <AlertTitle>{t.title}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc space-y-1 pl-4">
          {consequences.map((c) => (
            <li key={c.text}>{c.text}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
